import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Download, FileText, Users, ListChecks, AlertTriangle } from "lucide-react";
import { api, apiError, fmtDate } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { localeOf } from "../../i18n";

const Metric = ({ label, value, hint, testId, muted }) => (
  <div className="rounded-xl bg-muted p-4" data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-1 font-display text-3xl font-extrabold">{value}</p>
    {muted && (
      <span className="mt-1 inline-block rounded-full bg-[hsl(var(--warning))]/15 px-2 py-0.5 text-[11px] font-bold text-foreground">
        {muted}
      </span>
    )}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export default function DaySummary() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/events/${id}/day-summary`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e?.response?.status === 403 ? t("fieldLeads.forbidden") : apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const exportLeads = async () => {
    try {
      const { data: blob } = await api.get(`/events/${id}/leads.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `leads-${id}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("fieldLeads.exported"));
    } catch (e) {
      toast.error(e?.response?.status === 403 ? t("fieldLeads.forbidden") : apiError(e));
    }
  };

  if (!data) return <p className="py-10 text-center text-muted-foreground">{t("common.loading")}</p>;

  const { attendance: att, missions: mi, leads, points } = data;

  return (
    <div className="space-y-6" data-testid="day-summary-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
          {t("daySummary.title")}
        </p>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">{data.event.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {fmtDate(data.event.starts_at, locale)}
          {data.partner.name ? ` · ${data.partner.name}` : ""}
          {data.campaign.name ? ` · ${data.campaign.name}` : ""}
        </p>
        {data.team_leaders.length > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="day-summary-leaders">
            {t("daySummary.leaders")}: {data.team_leaders.map((l) => l.name || l.email).join(", ")}
          </p>
        )}
        {data.cancelled ? (
          <p className="mt-3 flex items-center gap-2 rounded-xl bg-destructive/10 p-3 text-sm font-bold text-destructive"
            data-testid="day-summary-cancelled">
            <AlertTriangle className="h-4 w-4" />{t("daySummary.cancelled")}
          </p>
        ) : !data.closed && (
          <p className="mt-3 rounded-xl bg-[hsl(var(--warning))]/10 p-3 text-sm font-bold"
            data-testid="day-summary-live">
            {t("daySummary.live")}
          </p>
        )}
      </div>

      <section data-testid="day-summary-attendance">
        <h2 className="font-display text-lg font-bold">{t("daySummary.attendance")}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Metric testId="day-att-registered" label={t("daySummary.registered")} value={att.registered} />
          <Metric testId="day-att-present" label={t("daySummary.present")} value={att.present} />
          <Metric testId="day-att-noshow" label={t("daySummary.noShow")} value={att.no_show} />
          <Metric testId="day-att-rate" label={t("daySummary.rate")} value={`${att.rate}%`} />
        </div>
      </section>

      <section data-testid="day-summary-missions">
        <h2 className="font-display text-lg font-bold">{t("daySummary.missions")}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Metric testId="day-mi-total" label={t("daySummary.missionsTotal")} value={mi.total} />
          <Metric testId="day-mi-approved" label={t("status.approved")} value={mi.approved} />
          <Metric testId="day-mi-pending" label={t("daySummary.pendingReview")} value={mi.pending_review}
            muted={t("daySummary.notCounted")} />
          <Metric testId="day-mi-rejected" label={t("status.rejected")} value={mi.rejected} />
          <Metric testId="day-mi-rate" label={t("daySummary.completion")} value={`${mi.completion_rate}%`}
            hint={t("daySummary.completionHint")} />
        </div>
      </section>

      <section data-testid="day-summary-leads">
        <h2 className="font-display text-lg font-bold">{t("daySummary.leads")}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-6">
          <Metric testId="day-lead-total" label={t("fieldLeads.total")} value={leads.total} />
          <Metric testId="day-lead-qr" label={t("fieldLeads.qr")} value={leads.qr_self_service} />
          <Metric testId="day-lead-manual" label={t("fieldLeads.manual")} value={leads.manual_ambassador} />
          <Metric testId="day-lead-offline" label={t("fieldLeads.offline")} value={leads.captured_offline} />
          <Metric testId="day-lead-pending" label={t("fieldLeads.pending")} value={leads.pending_sync}
            muted={t("daySummary.notConfirmed")} />
          <Metric testId="day-lead-confirmed" label={t("fieldLeads.confirmed")} value={leads.confirmed} />
        </div>
      </section>

      <section data-testid="day-summary-points">
        <h2 className="font-display text-lg font-bold">{t("daySummary.points")}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Metric testId="day-pt-total" label={t("daySummary.pointsTotal")} value={points.total} />
          <Metric testId="day-pt-attendance" label={t("progress.attendancePoints", { n: "" })}
            value={points.attendance} />
          <Metric testId="day-pt-missions" label={t("progress.missionPoints", { n: "" })}
            value={points.missions} />
          <Metric testId="day-pt-manual" label={t("daySummary.pointsManual")} value={points.manual} />
        </div>
      </section>

      <section className="flex flex-wrap gap-3" data-testid="day-summary-actions">
        <Button data-testid="day-export-leads-btn" onClick={exportLeads}
          className="h-12 rounded-full font-bold">
          <Download className="mr-2 h-4 w-4" />{t("fieldLeads.export")}
        </Button>
        <Link to={`/staff/events/${data.event.id}/report`} data-testid="day-partner-report-link">
          <Button variant="outline" className="h-12 rounded-full font-bold">
            <FileText className="mr-2 h-4 w-4" />{t("daySummary.partnerReport")}
          </Button>
        </Link>
        <Link to={`/staff/events/${data.event.id}`} data-testid="day-participants-link">
          <Button variant="outline" className="h-12 rounded-full font-bold">
            <Users className="mr-2 h-4 w-4" />{t("daySummary.participants")}
          </Button>
        </Link>
        <Link to="/staff/review" data-testid="day-review-link">
          <Button variant="outline" className="h-12 rounded-full font-bold">
            <ListChecks className="mr-2 h-4 w-4" />{t("daySummary.reviewQueue")}
          </Button>
        </Link>
      </section>

      <p className="text-xs text-muted-foreground" data-testid="day-summary-generated">
        {t("daySummary.generated")}: {fmtDate(data.generated_at, locale)}
      </p>
    </div>
  );
}
